import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import AdminPage from '../components/AdminShell';

// Avery 8066 sheet layout — 30 labels per sheet, matches the PDF the
// backend renders (see backend/app/services/wristbands.py LABELS_PER_PAGE).
const LABELS_PER_SHEET = 30;
const BAND_MIN = 1;
const BAND_MAX = 9999;

// Zero-pad to 3 digits — matches what actually prints on the band
// (see backend/app/core/bands.py format_band_number).
const pad = (n) => String(n).padStart(3, '0');

export default function AdminWristbandsPage() {
  const { API_URL } = useAuth();
  const [start, setStart] = useState(101);
  const [count, setCount] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const startNum = Number(start) || 0;
  const countNum = Number(count) || 0;
  const lastNum = startNum + countNum - 1;
  const sheets = countNum > 0 ? Math.ceil(countNum / LABELS_PER_SHEET) : 0;

  const getAxiosConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    params: { start: startNum, count: countNum },
    responseType: 'blob',
  });

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/admin/wristbands.pdf`, getAxiosConfig());
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `prozpects-wristbands-${pad(startNum)}-${pad(lastNum)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // responseType: 'blob' means an error body also arrives as a Blob
      // instead of parsed JSON — read it back and parse it ourselves so the
      // 409 collision list (taken band numbers) actually reaches the admin
      // instead of collapsing into a generic failure message.
      let detail = 'Could not generate the sheet. Please try again.';
      try {
        const text = await e.response.data.text();
        const parsed = JSON.parse(text);
        const d = parsed.detail;
        if (d && Array.isArray(d.taken) && d.taken.length > 0) {
          detail = `Band numbers ${d.taken.join(', ')} are already in use by real accounts. Pick a start/count that avoids them.`;
        } else if (d && typeof d === 'object' && d.message) {
          detail = d.message;
        } else if (typeof d === 'string') {
          detail = d;
        }
      } catch (_parseErr) {
        // Not a JSON blob (e.g. a network error with no response body) —
        // fall through to the generic message set above.
      }
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminPage
      // Reached from the Camps page header, not the rail (David, 2026-10-29), so it lights Camps and
      // its breadcrumb leads back there.
      active="camps"
      breadcrumb={[{ label: 'Camps', to: '/camps' }, { label: 'Wristbands' }]}
      title="Print Wristbands"
      subtitle={`Avery 8066 label sheets, ${LABELS_PER_SHEET} bands per sheet. Each band prints its `
        + 'number and four scannable codes for camp check-in.'}
    >

      <div className="card" style={{ maxWidth: 480 }}>
        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="band-start">Start band #</label>
            <input
              id="band-start"
              className="form-field__input"
              type="number"
              min={BAND_MIN}
              max={BAND_MAX}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="band-count">How many</label>
            <input
              id="band-count"
              className="form-field__input"
              type="number"
              min="1"
              max={BAND_MAX}
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
          </div>
        </div>

        <p style={{ fontSize: 14, color: '#475569', margin: '0 0 16px' }}>
          {sheets > 0 && countNum > 0 ? (
            <>
              <strong>{sheets} sheet{sheets === 1 ? '' : 's'}</strong>
              {' — bands '}
              <strong>{pad(startNum)}</strong>
              {' to '}
              <strong>{pad(lastNum)}</strong>
            </>
          ) : (
            'Enter a start number and count to preview the sheet.'
          )}
        </p>

        {error && <div className="adm-error">{error}</div>}

        <Button
          variant="primary"
          onClick={download}
          disabled={busy || countNum < 1 || startNum < BAND_MIN}
          loading={busy}
          loadingText="Generating…"
        >
          Download PDF
        </Button>
      </div>
    </AdminPage>
  );
}
